<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import type { Id, ObjectRecord } from '@vp/shared-types';
import { boxBounds } from '@vp/core';
import { live, ui } from '../editor/bridge';
import { t, tn, typeLabel } from '../i18n';
import Icon from './Icon.vue';
import { modulesState, moduleTypeLabel } from '../modules';
import { mainColor, objectText, snippet } from '../editor/search';

/**
 * Find on canvas (Ctrl+F): all text on the board, including text drawn by modules and schemes; jumps
 * between results. Filters narrow it to object types, colors and labels (they also work without text).
 */
const emit = defineEmits<{ close: [] }>();
const query = ref('');
const index = ref(0);
const input = ref<HTMLInputElement | null>(null);

const filters = reactive({ open: false, types: new Set<string>(), colors: new Set<string>() });
const filterCount = computed(() => filters.types.size + filters.colors.size);

/** What the canvas has to filter by: types and main colors, the most used first. */
const facets = computed(() => {
  void ui.docRev;
  const ed = live.editor;
  const types = new Map<string, number>();
  const colors = new Map<string, number>();
  for (const o of ed?.doc.allObjects() ?? []) {
    if (o.type === 'core:group') continue;
    types.set(o.type, (types.get(o.type) ?? 0) + 1);
    const c = mainColor(o);
    if (c) colors.set(c, (colors.get(c) ?? 0) + 1);
  }
  const top = (m: Map<string, number>, n: number) => [...m].sort((a, b) => b[1] - a[1]).slice(0, n);
  return { types: top(types, 16), colors: top(colors, 18) };
});

function passes(o: ObjectRecord): boolean {
  if (filters.types.size && !filters.types.has(o.type)) return false;
  if (filters.colors.size) {
    const c = mainColor(o);
    if (!c || !filters.colors.has(c)) return false;
  }
  return true;
}
function flip(set: Set<string>, v: string): void {
  if (set.has(v)) set.delete(v);
  else set.add(v);
}
function resetFilters(): void {
  filters.types.clear();
  filters.colors.clear();
}

const results = computed<Id[]>(() => {
  void ui.docRev;
  void modulesState.textRev;
  void filterCount.value;
  const q = query.value.trim().replace(/\s+/g, ' ').toLowerCase();
  const ed = live.editor;
  if ((!q && !filterCount.value) || !ed) return [];
  const hits: ObjectRecord[] = [];
  for (const o of ed.doc.allObjects()) if (o.type !== 'core:group' && !ed.scene.isFolded(o.id) && passes(o) && (!q || objectText(ed, o).text.includes(q))) hits.push(o);
  // reading order: top-to-bottom, left-to-right
  hits.sort((a, b) => a.y - b.y || a.x - b.x);
  return hits.slice(0, 1000).map((o) => o.id);
});

/** The matching piece of text of the current result. */
const context = computed(() => {
  void ui.docRev;
  const ed = live.editor;
  const id = results.value[index.value];
  const o = id && ed ? ed.doc.get(id) : undefined;
  const q = query.value.trim().replace(/\s+/g, ' ').toLowerCase();
  return o && ed && q ? snippet(objectText(ed, o).text, q) : '';
});

/** The first jump of a query is remembered; further typing only moves the camera. */
let remembered = false;

function show(i: number, record = true): void {
  const ids = results.value;
  const ed = live.editor;
  if (!ids.length || !ed) return;
  index.value = ((i % ids.length) + ids.length) % ids.length;
  const id = ids[index.value];
  ed.select([id]);
  const o = ed.doc.get(id);
  const cam = live.renderer?.camera;
  if (!o || !cam) return;
  const fit = () => cam.fit(boxBounds(o), 160, Math.max(1, cam.zoom));
  if (record) ed.reveal(fit);
  else fit();
}

function follow(fresh: boolean): void {
  index.value = 0;
  if (!results.value.length) return;
  if (fresh) remembered = false;
  show(0, !remembered);
  remembered = true;
}

watch(query, () => follow(false));
watch(filterCount, () => follow(true));

function onKey(e: KeyboardEvent): void {
  e.stopPropagation();
  if (e.key === 'Escape') emit('close');
  else if (e.key === 'Enter') show(index.value + (e.shiftKey ? -1 : 1));
}

onMounted(async () => {
  await nextTick();
  input.value?.focus();
  input.value?.select();
});
</script>

<template>
  <div class="float pillbar search" :class="{ low: ui.barBelow }" role="search" @pointerdown.stop>
    <Icon name="search" :size="16" />
    <input ref="input" v-model="query" type="search" :placeholder="t('search.placeholder')" :aria-label="t('search.placeholder')" @keydown="onKey" />
    <span class="count" aria-live="polite">{{ query || filterCount ? (results.length ? `${index + 1} / ${results.length}` : t('search.none')) : '' }}</span>
    <button class="icon-btn" :disabled="!results.length" :aria-label="t('search.prev')" :title="t('search.prev')" @click="show(index - 1)"><Icon name="chevronLeft" :size="14" /></button>
    <button class="icon-btn" :disabled="!results.length" :aria-label="t('search.next')" :title="t('search.next')" @click="show(index + 1)"><Icon name="chevron" :size="14" /></button>
    <button class="icon-btn filter-btn" :class="{ active: filters.open || filterCount }" :aria-label="t('search.filters')" :title="t('search.filters')" :aria-expanded="filters.open" data-act="search-filters" @click="filters.open = !filters.open">
      <Icon name="filter" :size="14" /><span v-if="filterCount" class="badge">{{ filterCount }}</span>
    </button>
    <button class="icon-btn" :aria-label="t('common.close')" @click="emit('close')"><Icon name="close" :size="14" /></button>
    <span class="sr-only">{{ tn('home.objects', results.length) }}</span>
    <span v-if="context && !filters.open" class="context" :title="context">{{ context }}</span>
    <div v-if="filters.open" class="float card filters" role="group" :aria-label="t('search.filters')">
      <p class="sec">{{ t('search.byType') }}</p>
      <div class="chips">
        <button v-for="[type, n] in facets.types" :key="type" class="pill" :class="{ active: filters.types.has(type) }" :aria-pressed="filters.types.has(type)" :data-type="type" @click="flip(filters.types, type)">
          {{ typeLabel(type, moduleTypeLabel) }}<span class="n">{{ n }}</span>
        </button>
      </div>
      <template v-if="facets.colors.length">
        <p class="sec">{{ t('search.byColor') }}</p>
        <div class="chips">
          <button v-for="[c] in facets.colors" :key="c" class="swatch-btn ring" :class="{ active: filters.colors.has(c) }" :style="{ background: c }" :aria-pressed="filters.colors.has(c)" :aria-label="c" :title="c" :data-color="c" @click="flip(filters.colors, c)"></button>
        </div>
      </template>
      <button v-if="filterCount" class="pill reset" @click="resetFilters">{{ t('search.resetFilters') }}</button>
    </div>
  </div>
</template>

<style scoped>
.search {
  top: 60px;
  left: 12px;
  padding: 4px 4px 4px 14px;
  gap: 6px;
  z-index: 26;
  color: var(--text-3);
}
input {
  border: none;
  outline: none;
  background: none;
  width: 220px;
  font-size: 13px;
  color: var(--text);
}
.count {
  min-width: 56px;
  font-size: 12px;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.icon-btn {
  width: 28px;
  height: 28px;
}
/* the properties bar moved below the top pills: the search goes under it */
.search.low {
  top: 112px;
}
.filter-btn {
  position: relative;
}
.filter-btn .badge {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 14px;
  height: 14px;
  border-radius: var(--radius-control);
  background: var(--accent);
  color: var(--on-accent, #fff);
  font-size: 9.5px;
  line-height: 14px;
  text-align: center;
}
.filters {
  position: absolute;
  left: 0;
  top: calc(100% + 8px);
  width: min(400px, calc(100vw - 24px));
  padding: 10px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: var(--text);
}
.filters .sec {
  margin: 4px 0 0;
  font-size: 11px;
  font-weight: 600;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}
.chips .pill {
  height: 26px;
  padding: 0 10px;
  font-size: 12px;
  gap: 5px;
  border: 1px solid var(--border);
}
.chips .pill.active {
  border-color: var(--accent);
}
.n {
  color: var(--muted);
  font-size: 11px;
}
.chips .swatch-btn.active {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
.reset {
  align-self: flex-start;
  margin-top: 4px;
  height: 26px;
  font-size: 12px;
}
.context {
  position: absolute;
  left: 14px;
  right: 14px;
  top: calc(100% + 6px);
  padding: 5px 10px;
  border-radius: var(--radius-panel);
  background: var(--float);
  border: 1px solid var(--float-border);
  box-shadow: var(--float-shadow);
  font-size: 12px;
  color: var(--text-2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
